// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Plan-Ansicht: Tage, Block-Gruppen, Aufklapp-Details je Übung.
// Zwei Modi: /plan zeigt den aktiven Plan, /plaene/:id jeden Plan im Detail
// VOR der Wahl (Thors Feedback Runde 7) — wählen/abwandeln/löschen direkt hier.
import { Fragment, useEffect, useState } from "react"
import { Link, Navigate, useNavigate, useParams } from "react-router-dom"
import type { Exercise, Plan, PlanDay, PlanDayItem } from "@/lib/types"
import { getPlans, getPlanDays, getPlanDayItems, getExercises, activePlanId, onSynced, sofort, nachId } from "@/lib/data"
import { planBadge, setActivePlan, forkPlan, deletePlan } from "@/lib/plans"
import { toast } from "@/lib/toast"
import { parseBeschreibung } from "@/lib/beschreibung"
import { blockDe } from "@/lib/labels"
import { videoUrl } from "@/lib/video"
import VideoPanel from "@/components/VideoPanel"
import Wichtel from "@/components/celebration/creatures/Wichtel"
import "@/styles/plan.css"

const OFFLINE_MSG = "Dafür brauchst du Internet – bitte später erneut versuchen"

/** /plaene/:id zeigt genau diesen Plan, /plan den gewählten (sonst die erste Vorlage). */
function gezeigterPlan(plans: Plan[] | null, id: string | undefined): Plan | null {
  if (!plans) return null
  if (id) return plans.find((p) => p.id === id) ?? null
  return plans.find((p) => p.id === activePlanId()) ?? plans.find((p) => !p.owner) ?? plans[0] ?? null
}

const tageVon = (days: PlanDay[] | null, plan: Plan | null): PlanDay[] =>
  (days ?? []).filter((d) => d.plan === plan?.id).sort((a, b) => a.sort - b.sort)

export default function PlanPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  // Sofort-Speicher: beim Tab-Wechsel steht die Seite schon (Runde 16)
  const [loaded, setLoaded] = useState(() => sofort.plans() !== null)
  const [plan, setPlan] = useState<Plan | null>(() => gezeigterPlan(sofort.plans(), id))
  const [days, setDays] = useState<PlanDay[]>(() => tageVon(sofort.planDays(), gezeigterPlan(sofort.plans(), id)))
  const [items, setItems] = useState<PlanDayItem[]>(() => sofort.planDayItems() ?? [])
  const [exercises, setExercises] = useState<Map<string, Exercise>>(() => nachId(sofort.exercises()))
  const [openId, setOpenId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)

  useEffect(() => {
    const load = async () => {
      const [plans, allDays, allItems, exs] = await Promise.all([
        getPlans(), getPlanDays(), getPlanDayItems(), getExercises(),
      ])
      const gezeigt = gezeigterPlan(plans, id)
      setPlan(gezeigt)
      setDays(tageVon(allDays, gezeigt))
      setItems(allItems)
      setExercises(nachId(exs))
      setLoaded(true)
    }
    void load()
    return onSynced(() => { void load() })
  }, [id])

  async function waehlen(p: Plan) {
    if (busy) return
    setBusy(true)
    try {
      await setActivePlan(p.id)
      toast("Plan gewählt ✓")
      navigate("/plan")
    } catch {
      toast(OFFLINE_MSG)
    } finally {
      setBusy(false)
    }
  }

  // Fork ist folgenlos fürs Original — kein confirm() (wird in der App verschluckt)
  async function abwandeln(p: Plan) {
    if (planBadge(p) === "dein Plan") {
      navigate(`/plaene/${p.id}/bearbeiten`)
      return
    }
    if (busy) return
    setBusy(true)
    try {
      const newId = await forkPlan(p.id)
      toast("Eigene Kopie erstellt ✓ – das Original bleibt unverändert")
      navigate(`/plaene/${newId}/bearbeiten`)
    } catch {
      toast(OFFLINE_MSG)
    } finally {
      setBusy(false)
    }
  }

  async function loeschen(p: Plan) {
    try {
      await deletePlan(p.id)
      toast("Plan gelöscht")
      navigate("/plaene")
    } catch {
      toast(OFFLINE_MSG)
    }
  }

  if (!loaded) return null
  if (!plan) {
    // Detail-Modus mit unbekannter Id (z. B. gelöschter Plan) → zurück zur Liste
    if (id) return <Navigate to="/plaene" replace />
    return (
      <div className="empty">
        <div className="critter"><Wichtel /></div>
        <p><b>Kein Plan? Bau dir einen.</b></p>
        <Link to="/plaene" className="btn-secondary pressable">Plan anlegen</Link>
      </div>
    )
  }

  const detail = Boolean(id)
  const istAktiv = plan.id === activePlanId()
  const eigen = planBadge(plan) === "dein Plan"

  const bloecke = parseBeschreibung(plan.description ?? "")
  const erster = bloecke[0]
  const intro = erster && erster.art === "absatz" ? erster : null
  const details = intro ? bloecke.slice(1) : bloecke

  return (
    <>
      {detail && <p className="plan-back"><Link to="/plaene">‹ Alle Pläne</Link></p>}

      {/* Die EINE Hero-Karte der Seite (Skill §4): nur Name + Kurzintro.
          Regeln & Details stehen formatiert DARUNTER auf dem Papier — kein
          Textblock-Grab mehr in der Karte (Thors Feedback Runde 4). */}
      <div className="card--hero plan-hero">
        <div className="plan-title">
          <b>{plan.name}</b>
          <span className="plan-badge">{planBadge(plan)}</span>
        </div>
        {intro && <p className="plan-desc">{intro.text}</p>}
        {!detail && (
          <Link to="/plaene" className="btn-secondary plan-switch">Plan wechseln oder anpassen</Link>
        )}
        {detail && istAktiv && <p className="plan-aktiv-note">✓ Dein aktiver Plan</p>}
        {detail && !istAktiv && (
          <button
            type="button"
            className="btn-secondary plan-switch"
            disabled={busy}
            onClick={() => void waehlen(plan)}
          >
            Als meinen Plan wählen
          </button>
        )}
        {detail && (
          <button
            type="button"
            className="btn-secondary plan-switch"
            disabled={busy}
            onClick={() => void abwandeln(plan)}
          >
            {eigen ? "Bearbeiten" : "Abwandeln"}
          </button>
        )}
      </div>

      {details.length > 0 && (
        <div className="plan-details">
          {details.map((b, i) =>
            b.art === "absatz" ? (
              <p key={i}>{b.text}</p>
            ) : (
              <section key={i}>
                {b.label && <h2>{b.label}</h2>}
                <ul className="plan-regeln">
                  {b.punkte.map((p, j) => <li key={j}>{p}</li>)}
                </ul>
              </section>
            ),
          )}
        </div>
      )}

      <div className="plan-days-grid">
        {days.map((d) => {
          const dayItems = items.filter((i) => i.plan_day === d.id).sort((a, b) => a.sort - b.sort)
          let prevBlock = ""
          return (
            <section key={d.id} className="plan-day">
              {/* Name nur zeigen, wenn er mehr sagt als „Tag A" */}
              <h2>{d.name && d.name !== `Tag ${d.code}` ? `Tag ${d.code} – ${d.name}` : `Tag ${d.code}`}</h2>
              {/* Gruppenliste statt Karte (Skill §13.7) — die Überschrift ersetzt den Rahmen */}
              <div className="plan-day-list">
                {!dayItems.length && <p className="hint">Noch keine Übungen an diesem Tag.</p>}
                {dayItems.map((it) => {
                  const ex = exercises.get(it.exercise)
                  const open = openId === it.id
                  const label = it.block !== "" && it.block !== prevBlock ? blockDe(it.block) : null
                  prevBlock = it.block
                  return (
                    <Fragment key={it.id}>
                      {label && <div className="plan-block-label">{label}</div>}
                      <div className="plan-item">
                        <button
                          type="button"
                          className="plan-item-head"
                          aria-expanded={open}
                          onClick={() => setOpenId(open ? null : it.id)}
                        >
                          <span className="plan-thumb" aria-hidden="true">
                            {ex && ex.media_slug !== "" && (
                              <img
                                src={`/media/exercises/${ex.media_slug}.jpg`}
                                alt=""
                                loading="lazy"
                                onError={(e) => { e.currentTarget.style.display = "none" }}
                              />
                            )}
                          </span>
                          <span className="name">{ex?.name ?? "Übung"}</span>
                          <span className="target">{it.target_sets}×{it.target_reps}</span>
                        </button>
                        {open && (
                          <div className="plan-item-body">
                            {ex && ex.media_slug !== "" && (
                              <img
                                src={`/media/exercises/${ex.media_slug}.gif`}
                                alt={ex.name}
                                width={180}
                                height={180}
                                loading="lazy"
                                onError={(e) => { e.currentTarget.style.display = "none" }}
                              />
                            )}
                            {ex && ex.benefit_text !== "" && <p><b>Was bringt's:</b> {ex.benefit_text}</p>}
                            {ex && ex.execution_text !== "" && <p><b>Ausführung:</b> {ex.execution_text}</p>}
                            {ex && ex.benefit_text === "" && ex.execution_text === "" && ex.instructions !== "" && (
                              <p><b>Ausführung:</b> {ex.instructions}</p>
                            )}
                            {it.timer && (
                              <p><b>Intervall:</b> {it.timer.rounds} × {it.timer.work_sec} Sek. / {it.timer.rest_sec} Sek. Pause</p>
                            )}
                            {ex && (ex.video_url
                              ? <VideoPanel url={ex.video_url} titel={ex.name} />
                              : <a href={videoUrl(ex)} target="_blank" rel="noreferrer">Video auf YouTube suchen ↗</a>)}
                          </div>
                        )}
                      </div>
                    </Fragment>
                  )
                })}
              </div>
            </section>
          )
        })}
      </div>

      {detail && eigen && (
        confirmDel ? (
          /* Destruktives = Inline-Zweischritt, nie confirm() (Skill §13.6) */
          <div className="plan-del-confirm">
            <span>Diesen Plan wirklich löschen?</span>
            <button type="button" className="btn-secondary" onClick={() => setConfirmDel(false)}>Behalten</button>
            <button type="button" className="btn-secondary del-yes" onClick={() => void loeschen(plan)}>Ja, löschen</button>
          </div>
        ) : (
          <button type="button" className="plan-del-link" onClick={() => setConfirmDel(true)}>Plan löschen</button>
        )
      )}

      <p className="hint plan-footnote">Kein medizinischer Rat – bei Schmerzen ärztlich oder physiotherapeutisch abklären.</p>
    </>
  )
}
